import React from 'react';
import { Flag, Info, Sparkles } from 'lucide-react';
import { RegulationLink } from '../ui/RegulationLink';
type SuggestedClaim = {
  title: string;
  rationale: string;
};
const suggestedClaims: SuggestedClaim[] = [{
  title: 'Maintain/support immune system health',
  rationale: 'Vitamin A, vitamin C and zinc all contribute to the normal function of the immune system.'
}, {
  title: 'Maintain/support healthy eye function',
  rationale: 'Vitamin A is essential for the production of rhodopsin, a pigment required for vision in low light.'
}, {
  title: 'Maintain/support collagen formation',
  rationale: 'Vitamin C is required for the normal formation of collagen.'
}, {
  title: 'Antioxidant/Reduce free radicals formed in the body',
  rationale: 'Vitamin C and beta carotene both act to scavenge free radicals.'
}, {
  title: 'Maintain/support healthy skin',
  rationale: 'Vitamin A and vitamin C both contribute to the maintenance of normal skin.'
}, {
  title: 'Maintain/support wound healing',
  rationale: 'Zinc and vitamin C contribute to normal tissue repair.'
}, {
  title: 'Maintain/support healthy mucous membranes',
  rationale: 'Vitamin A supports the maintenance of normal epithelial tissues.'
}, {
  title: 'Maintain/support connective tissue health',
  rationale: 'Vitamin C is required for normal connective tissue formation.'
}];
interface SuggestedClaimsTabProps {
  market?: 'AU' | 'SG';
}
export function SuggestedClaimsTab({
  market = 'AU'
}: SuggestedClaimsTabProps) {
  const isSingapore = market === 'SG';
  const citation = isSingapore ? {
    text: 'HSA Guidelines for Claims and Claims Substantiation of Health Supplements and Traditional Medicines, January 2025',
    url: 'https://www.hsa.gov.sg/docs/default-source/hprg-tmhs/chpb-tmhs/tmhs_claims_guidelines.pdf'
  } : {
    text: 'Therapeutic Goods (Permissible Indications) Determination',
    url: 'https://www.tga.gov.au/resources/legislation/therapeutic-goods-permissible-indications-determination'
  };
  return <div className="mx-auto max-w-5xl" data-id="element-2447" data-mp="2na04tumal3ve">
      <header data-id="element-2448" data-mp="2nb1fvz3b5qd4">
        <div className="flex items-center gap-3" data-id="element-2449" data-mp="2ra0ihd1khb1e">
          <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#eff3ed] text-forest-green" data-id="element-2450" data-mp="2rasegoz2tas7"><Sparkles className="h-5 w-5" data-id="element-2451" data-mp="2w7sb69a1d16n" /></span>
          <div data-id="element-2452" data-mp="3186l5wv76l7b">
            <h2 className="font-[Figtree] text-2xl font-semibold text-evergreen" data-id="element-2453" data-mp="318q1xa9433jr">Suggested claims</h2>
            <p className="mt-1 text-sm text-[#6b7d70]" data-id="element-2454" data-mp="368ceril12hs1">Claims this product may be eligible to make, based on its composition.</p>
          </div>
        </div>
        <p className="mt-6 font-[Figtree] text-lg font-semibold text-evergreen" data-id="element-2455" data-mp="3a6rzfftmze2a">{isSingapore ? '8 ready to use' : '8 need substantiation'}</p>
      </header>

      <section className="mt-5" aria-labelledby="substantiation-heading" data-id="element-2456" data-mp="3jxmlok6vudnq">
        <h3 id="substantiation-heading" className="inline-flex items-center gap-2 font-mono text-xs font-semibold uppercase tracking-[0.15em] text-[#708174]" data-id="element-2457" data-mp="3oykavgqb87yq"><span className={`h-2 w-2 rounded-full ${isSingapore ? 'bg-forest-green' : 'bg-amber-500'}`} data-id="element-2458" data-mp="3txdjovunjask" />{isSingapore ? 'Ready to use · 8' : 'Needs substantiation · 8'}</h3>
        <div className={`mt-3 flex items-start gap-2 rounded-xl border px-4 py-3 text-sm leading-relaxed ${isSingapore ? 'border-[#dbe4d8] bg-[#f5f8f3] text-[#5c6e61]' : 'border-amber-200 bg-amber-50 text-amber-900'}`} data-id="element-2459" data-mp="1d9w0en2x48cr">
          <Info className={`mt-0.5 h-4 w-4 shrink-0 ${isSingapore ? 'text-[#718174]' : 'text-amber-700'}`} data-id="element-2460" data-mp="13ec788fqnnrp" />
          {isSingapore ? 'Singapore does not operate a closed list of permitted indications for health supplements. Structural and health claims suggesting maintenance and support of good health are acceptable, provided the company holds supporting evidence.' : 'Suggested claims are drawn from composition only. Indications for a listed medicine must be selected verbatim from the Permissible Indications Determination — confirm the exact listed wording before use.'}
        </div>
        <div className="mt-3 overflow-hidden rounded-2xl border border-[#dce3da] bg-white shadow-sm" data-id="element-2461" data-mp="2exy733oz8i3y">
          {suggestedClaims.map((claim, index) => <article key={claim.title} className={`px-5 py-4 ${index < suggestedClaims.length - 1 ? 'border-b border-[#edf0eb]' : ''}`} data-id="element-2462" data-mp="24i5mbx4eh85y" data-mpi={index}>
              <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between" data-id="element-2463" data-mp="1ply6ta3xsux0">
                <h4 className="font-[Figtree] text-base font-semibold text-evergreen" data-id="element-2464" data-mp="1ul10xzidpgq7">{claim.title}</h4>
                <span className="shrink-0 rounded-md bg-[#f0f3ef] px-2 py-1 font-mono text-[10px] font-semibold uppercase tracking-[0.13em] text-[#637467]" data-id="element-2465" data-mp="2ialt990f9qc5">Therapeutic</span>
              </div>
              <p className="mt-2 text-sm leading-relaxed text-[#6a7a6d]" data-id="element-2466" data-mp="28gmy4tyrn6tp">{claim.rationale}</p>
              {!isSingapore && <p className="mt-3 inline-flex items-center gap-2 text-sm font-medium text-[#617366]" data-id="element-2467" data-mp="2dfg1htkc9hg5"><Flag className="h-4 w-4 text-[#78877b]" data-id="element-2468" data-mp="315abm3d9wrwp" />Needs: substantiating evidence</p>}
              <div className="mt-3" data-id="element-2469" data-mp="35zekwta0hc0y">
                <RegulationLink text={citation.text} href={citation.url} data-id="element-2470" data-mp="uiutq4yudscb" />
              </div>
            </article>)}
        </div>
      </section>

      <footer className="mt-5 flex items-start gap-2 rounded-xl border border-[#dbe4d8] bg-[#f5f8f3] px-4 py-3 text-sm leading-relaxed text-[#607266]" data-id="element-2471" data-mp="pjrnih74lg2r">
        <Info className="mt-0.5 h-4 w-4 shrink-0 text-[#718174]" data-id="element-2472" data-mp="z9b8ptib9m0s" />
        Suggestions only — not a verification. Each claim must be confirmed and, where indicated, substantiated before use.
      </footer>
    </div>;
}